import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconAlertCircle, IconChevronLeft, IconUsers, IconCash, IconNotes,
  IconCheck, IconPlus, IconUserOff, IconPencil,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import { useManagerApproval } from '../../hooks/useManagerApproval';

interface Member {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  isActive: boolean;
}

interface ApiAccount {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  creditLimitCents: number;
  balanceCents: number;
  members: Member[];
}

interface StatementRow {
  kind: 'charge' | 'payment';
  at: string;
  amountCents: number;
  reference: string;
  detail: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Africa/Johannesburg' });
}

export default function AdminAccountDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const askApproval = useManagerApproval();
  const [account, setAccount] = useState<ApiAccount | null>(null);
  const [statement, setStatement] = useState<StatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [payDownOpen, setPayDownOpen] = useState(false);
  const [creditLimitOpen, setCreditLimitOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        fetch(`/api/accounts/${id}`, { credentials: 'include' }),
        fetch(`/api/accounts/${id}/statement`, { credentials: 'include' }),
      ]);
      if (!a.ok) throw new Error(`HTTP ${a.status}`);
      const ad = await a.json();
      setAccount(ad.account);
      if (s.ok) {
        const sd = await s.json();
        setStatement(sd.rows ?? []);
      }
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !account) {
    return <AdminLayout title="Account"><p className="text-13 italic text-gray-700">Loading…</p></AdminLayout>;
  }

  const available = Math.max(0, account.creditLimitCents - account.balanceCents);
  const activeMembers = account.members.filter((m) => m.isActive);

  return (
    <AdminLayout
      title={account.name}
      subtitle={`Internal account · ${activeMembers.length} active member${activeMembers.length === 1 ? '' : 's'}`}
      actions={
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setRenameOpen(true)} className="h-10 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100">
            <IconPencil size={14} stroke={2.25} /> Rename
          </button>
          <button type="button" onClick={() => navigate('/admin/accounts')} className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100">
            <IconChevronLeft size={14} stroke={2.5} />
            All accounts
          </button>
        </div>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-5 mb-5">
        <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Balance</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Tile label="Balance" value={fmtR(account.balanceCents)} tone={account.balanceCents > 0 ? 'warning' : 'default'} bold />
          <Tile label="Credit limit" value={fmtR(account.creditLimitCents)} />
          <Tile label="Available" value={fmtR(available)} tone={available === 0 ? 'danger' : 'success'} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setPayDownOpen(true)} className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-success text-[#FFFFFF] text-13 font-bold hover:bg-success-hover shadow-sm">
            <IconCash size={14} stroke={2.5} /> Receive payment
          </button>
          <button type="button" onClick={() => setCreditLimitOpen(true)} className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100">
            <IconNotes size={14} stroke={2.5} /> Change credit limit
          </button>
        </div>
      </section>

      <section className="rounded-lg bg-surface border border-border p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-2">
            <IconUsers size={14} stroke={2.25} />
            Members
          </h2>
          <button type="button" onClick={() => setAddMemberOpen(true)} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-primary-soft text-primary text-12 font-bold uppercase tracking-wider hover:bg-primary hover:text-[#FFFFFF]">
            <IconPlus size={14} stroke={2.5} /> Add member
          </button>
        </div>
        {account.members.length === 0 ? (
          <p className="text-13 italic text-gray-700">No members yet — add at least one so cashiers know who placed each order.</p>
        ) : (
          <ul className="space-y-2">
            {account.members.map((m) => (
              <li key={m.id} className={[
                'flex items-center gap-3 p-3 rounded-md border',
                m.isActive ? 'bg-light border-border-soft' : 'bg-light/50 border-border-soft opacity-60',
              ].join(' ')}>
                <div className="w-9 h-9 rounded-full bg-primary text-[#FFFFFF] inline-flex items-center justify-center text-13 font-bold flex-shrink-0">
                  {m.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-14 font-bold text-heading truncate">{m.name}</p>
                  <p className="text-12 font-medium text-gray-700 truncate">
                    {m.email && <span>{m.email}</span>}
                    {m.email && m.phone && <span> · </span>}
                    {m.phone && <span>{m.phone}</span>}
                    {!m.isActive && <span className="ml-2 text-danger font-bold uppercase tracking-wider">Inactive</span>}
                  </p>
                </div>
                {m.isActive && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!confirm(`Remove ${m.name} from this account?`)) return;
                      await fetch(`/api/accounts/${id}/members/${m.id}`, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ isActive: false }),
                      });
                      void load();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-danger-soft hover:text-danger"
                    title="Remove member"
                  >
                    <IconUserOff size={14} stroke={2.25} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg bg-surface border border-border p-5">
        <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Statement</h2>
        {statement.length === 0 ? (
          <p className="text-13 italic text-gray-700">No activity yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {statement.map((row, i) => (
              <li key={i} className="flex items-center gap-3 p-2.5 rounded-md bg-light border border-border-soft text-13">
                <span className={`inline-flex items-center px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider ${row.kind === 'charge' ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'}`}>
                  {row.kind === 'charge' ? 'Charge' : 'Payment'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-heading">{row.reference}</p>
                  {row.detail && <p className="text-12 text-gray-700 truncate">{row.detail}</p>}
                </div>
                <p className={`font-bold tabular-nums whitespace-nowrap ${row.amountCents > 0 ? 'text-warning' : 'text-success'}`}>
                  {row.amountCents > 0 ? '+' : ''}{fmtR(row.amountCents)}
                </p>
                <p className="text-11 text-gray-700 tabular-nums whitespace-nowrap">{fmtDateTime(row.at)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AddMemberModal open={addMemberOpen} onClose={() => setAddMemberOpen(false)} accountId={account.id} onSaved={() => { void load(); setAddMemberOpen(false); }} />
      <PayDownModal open={payDownOpen} onClose={() => setPayDownOpen(false)} accountId={account.id} balanceCents={account.balanceCents} onSaved={() => { void load(); setPayDownOpen(false); }} />
      <CreditLimitModal open={creditLimitOpen} onClose={() => setCreditLimitOpen(false)} accountId={account.id} currentLimit={account.creditLimitCents} askApproval={askApproval} onSaved={() => { void load(); setCreditLimitOpen(false); }} />
      <RenameModal open={renameOpen} onClose={() => setRenameOpen(false)} accountId={account.id} currentName={account.name} onSaved={() => { void load(); setRenameOpen(false); }} />
    </AdminLayout>
  );
}

function Tile({ label, value, bold, tone = 'default' }: { label: string; value: string; bold?: boolean; tone?: 'default' | 'success' | 'warning' | 'danger' }) {
  const cls = {
    default: 'bg-light border-border-soft text-heading',
    success: 'bg-success-soft border-success/30 text-success',
    warning: 'bg-warning-soft border-warning/30 text-warning',
    danger:  'bg-danger-soft border-danger/30 text-danger',
  }[tone];
  return (
    <div className={`rounded-md border p-3 ${cls}`}>
      <p className="text-11 font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className={`tabular-nums mt-0.5 ${bold ? 'text-20 font-bold' : 'text-16 font-bold'}`}>{value}</p>
    </div>
  );
}

function AddMemberModal({ open, onClose, accountId, onSaved }: { open: boolean; onClose: () => void; accountId: string; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setName(''); setEmail(''); setPhone(''); setErr(null); } }, [open]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`/api/accounts/${accountId}/members`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Add member">
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Name</span>
        <input type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Email (optional)</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      <label className="block mb-4">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Phone (optional)</span>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}
      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button onClick={save} disabled={saving || !name.trim()} className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
          <IconCheck size={16} stroke={2.5} />
          {saving ? 'Adding…' : 'Add member'}
        </button>
      </div>
    </Modal>
  );
}

function PayDownModal({ open, onClose, accountId, balanceCents, onSaved }: { open: boolean; onClose: () => void; accountId: string; balanceCents: number; onSaved: () => void }) {
  const [amountRand, setAmountRand] = useState('');
  const [method, setMethod] = useState<'CASH' | 'CARD_YOCO' | 'EFT' | 'OTHER'>('CASH');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setAmountRand((balanceCents / 100).toFixed(2)); setMethod('CASH'); setReference(''); setErr(null); } }, [open, balanceCents]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`/api/accounts/${accountId}/payments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method,
          amountCents: Math.round(Number(amountRand.replace(',', '.')) * 100),
          reference: reference.trim() || null,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Receive payment on account">
      <p className="text-13 text-gray-700 mb-4">Current balance: <strong>{fmtR(balanceCents)}</strong></p>
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Amount (R)</span>
        <input type="text" inputMode="decimal" autoFocus value={amountRand} onChange={(e) => setAmountRand(e.target.value.replace(/[^0-9.,]/g, ''))} className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading" />
      </label>
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Method</span>
        <select value={method} onChange={(e) => setMethod(e.target.value as 'CASH' | 'CARD_YOCO' | 'EFT' | 'OTHER')} className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border outline-none text-14 font-semibold text-heading">
          <option value="CASH">Cash</option>
          <option value="CARD_YOCO">Card</option>
          <option value="EFT">EFT</option>
          <option value="OTHER">Other</option>
        </select>
      </label>
      <label className="block mb-4">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Reference</span>
        <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}
      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button onClick={save} disabled={saving} className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-success text-[#FFFFFF] text-14 font-bold hover:bg-success-hover disabled:bg-gray-300">
          <IconCheck size={16} stroke={2.5} />
          {saving ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </Modal>
  );
}

function CreditLimitModal({ open, onClose, accountId, currentLimit, askApproval, onSaved }: { open: boolean; onClose: () => void; accountId: string; currentLimit: number; askApproval: (input: { action: string; description?: string }) => Promise<boolean>; onSaved: () => void }) {
  const [limitRand, setLimitRand] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setLimitRand((currentLimit / 100).toFixed(2)); setReason(''); setErr(null); } }, [open, currentLimit]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      if (!reason.trim()) throw new Error('Reason is required');
      const ok = await askApproval({ action: 'Change credit limit', description: `From ${fmtR(currentLimit)} to ${fmtR(Math.round(Number(limitRand.replace(',', '.')) * 100))}` });
      if (!ok) { setSaving(false); return; }
      const r = await fetch(`/api/accounts/${accountId}/credit-limit`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creditLimitCents: Math.round(Number(limitRand.replace(',', '.')) * 100), reason: reason.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Change credit limit">
      <label className="block mb-3">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">New limit (R)</span>
        <input type="text" inputMode="decimal" value={limitRand} onChange={(e) => setLimitRand(e.target.value.replace(/[^0-9.,]/g, ''))} className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading" />
      </label>
      <label className="block mb-4">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Reason <span className="text-danger">*</span></span>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}
      <p className="text-12 text-gray-700 mb-4">Manager PIN required.</p>
      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button onClick={save} disabled={saving || !reason.trim()} className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
          <IconCheck size={16} stroke={2.5} />
          {saving ? 'Saving…' : 'Save + manager PIN'}
        </button>
      </div>
    </Modal>
  );
}

function RenameModal({ open, onClose, accountId, currentName, onSaved }: { open: boolean; onClose: () => void; accountId: string; currentName: string; onSaved: () => void }) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) { setName(currentName); setErr(null); } }, [open, currentName]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`/api/accounts/${accountId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm" title="Rename account">
      <label className="block mb-4">
        <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">New name</span>
        <input type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
      </label>
      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}
      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button onClick={save} disabled={saving || !name.trim() || name.trim() === currentName} className="flex-[2] h-12 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
